<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useCommonStore } from '../stores/common';
import { useRouter } from 'vue-router';
import { useAlert } from '../composables/useAlert';

const auth = useAuthStore();
const common = useCommonStore();
const router = useRouter();
const alert = useAlert();
const isOpen = ref(false);

let stockInterval = null;

const checkLowStock = async () => {
  if (auth.branch?.cd) {
    const newlyAdded = await common.fetchLowStockItems(auth.branch.cd);
    
    if (newlyAdded && newlyAdded.length > 0) {
        // Show Toast
        newlyAdded.forEach(item => {
            alert.toast(`Low Stock: ${item.item_desc} (${item.bal} remaining)`, 'warning');
            
            // Try Browser Notification
            if (Notification.permission === "granted") {
                new Notification("IANKING Low Stock Alert", {
                    body: `${item.item_desc} is down to ${item.bal}. Reorder Level: ${item.stock_level}`,
                    icon: faviconUrl
                });
            }
        });
    }
  }
};

onMounted(() => {
  if (auth.isLoggedIn) {
    checkLowStock();
    // Check every 60 seconds
    stockInterval = setInterval(checkLowStock, 60000);
    
    // Request permission for browser notifications
    if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
    }
  }
});

watch(() => auth.branch, () => {
    checkLowStock();
});

onUnmounted(() => {
  if (stockInterval) {
    clearInterval(stockInterval);
  }
});

const adminMenu = [
  { name: 'Manage User', path: '/admin/users' },
  { name: 'Manage Employee', path: '/admin/employees' },
  { name: 'Product/Services', path: '/admin/services' },
  { name: 'Cash Flow', path: '/admin/cashflow' },
];

const tailoringMenu = [
  { name: 'Manage Customer', path: '/tailoring/customers' },
  { name: 'Job Orders', path: '/tailoring/job-orders' },
  { name: 'Sewer Tracking', path: '/tailoring/sewer' },
  { name: 'Sewer Reports', path: '/tailoring/sewer-reports' },
  { name: 'Release Schedule', path: '/tailoring/schedule' },
  { name: 'Repair Tracking', path: '/tailoring/repair' },
  { name: 'Daily Closing', path: '/tailoring/closing' },
];

const inventoryMenu = [
  { name: 'Items Masterlist', path: '/inventory/items' },
  { name: 'Log Receipt', path: '/inventory/receive' },
  { name: 'Stock Request', path: '/inventory/request' },
  { name: 'Stock Release', path: '/inventory/release' },
];

const payrollMenu = [
  { name: 'Payroll Periods', path: '/payroll' },
  { name: 'Cash Advance', path: '/payroll/cash-advance' },
];

const expensesMenu = [
  { name: 'Log Expenses', path: '/expenses' },
];

const logout = () => {
  auth.logout();
  router.push('/login');
};

const base = window.APP_IDENTITY?.ROUTER_BASE || '/';
const logoUrl = (base.endsWith('/') ? base : base + '/') + 'assets/images/logo.png';
const faviconUrl = (base.endsWith('/') ? base : base + '/') + 'favicon.ico';
</script>

<template>
  <nav class="bg-brand-emerald text-white sticky top-0 z-50 shadow-2xl border-b border-white/5 backdrop-blur-md">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-between h-20">
        <!-- Logo and Brand -->
        <div class="flex items-center space-x-6">
          <router-link to="/" class="flex items-center space-x-4 group">
            <div class="h-14 w-14 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 transition-all group-hover:border-brand-gold/50 group-hover:bg-white/10 shadow-lg group-hover:shadow-brand-gold/10">
              <img :src="logoUrl" alt="Logo" class="h-10 w-auto" />
            </div>
            <div class="flex flex-col">
              <span class="font-black text-xl tracking-[0.1em] leading-none text-white group-hover:text-brand-gold transition-colors">IAN KING</span>
              <span class="text-[9px] font-black text-brand-gold/70 uppercase tracking-[0.25em] mt-1.5 flex items-center gap-1.5">
                {{ auth.branch.name }}
              </span>
            </div>
          </router-link>
          
          <div class="hidden lg:flex items-center space-x-3">
            <!-- Admin Dropdown -->
            <div v-if="auth.isAdmin" class="relative group">
              <button class="px-4 py-2 rounded-xl text-sm font-black hover:bg-white/10 transition flex items-center gap-2 group-hover:text-brand-gold">
                Admin
                <svg class="w-4 h-4 text-white/30 group-hover:text-brand-gold/50 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 9l-7 7-7-7" stroke-width="2.5" /></svg>
              </button>
              <div class="absolute left-0 mt-2 w-56 bg-brand-emerald/95 backdrop-blur-2xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-3 transform translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                <router-link v-for="item in adminMenu" :key="item.path" :to="item.path" class="block px-5 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-brand-gold hover:text-brand-dark transition-colors">{{ item.name }}</router-link>
              </div>
            </div>

            <!-- Tailoring Dropdown -->
            <div class="relative group">
              <button class="px-4 py-2 rounded-xl text-sm font-black hover:bg-white/10 transition flex items-center gap-2 group-hover:text-brand-gold">
                Tailoring
                <svg class="w-4 h-4 text-white/30 group-hover:text-brand-gold/50 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 9l-7 7-7-7" stroke-width="2.5" /></svg>
              </button>
              <div class="absolute left-0 mt-2 w-56 bg-brand-emerald/95 backdrop-blur-2xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-3 transform translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                <router-link v-for="item in tailoringMenu" :key="item.path" :to="item.path" class="block px-5 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-brand-gold hover:text-brand-dark transition-colors">{{ item.name }}</router-link>
              </div>
            </div>

            <!-- Inventory Dropdown -->
            <div class="relative group">
              <button class="px-4 py-2 rounded-xl text-sm font-black hover:bg-white/10 transition flex items-center gap-2 group-hover:text-brand-gold">
                Inventory
                <svg class="w-4 h-4 text-white/30 group-hover:text-brand-gold/50 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 9l-7 7-7-7" stroke-width="2.5" /></svg>
              </button>
              <div class="absolute left-0 mt-2 w-56 bg-brand-emerald/95 backdrop-blur-2xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-3 transform translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                <router-link v-for="item in inventoryMenu" :key="item.path" :to="item.path" class="block px-5 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-brand-gold hover:text-brand-dark transition-colors">{{ item.name }}</router-link>
              </div>
            </div>

            <!-- Payroll Dropdown -->
            <div class="relative group">
              <button class="px-4 py-2 rounded-xl text-sm font-black hover:bg-white/10 transition flex items-center gap-2 group-hover:text-brand-gold">
                Payroll
                <svg class="w-4 h-4 text-white/30 group-hover:text-brand-gold/50 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 9l-7 7-7-7" stroke-width="2.5" /></svg>
              </button>
              <div class="absolute left-0 mt-2 w-56 bg-brand-emerald/95 backdrop-blur-2xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-3 transform translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                <router-link v-for="item in payrollMenu" :key="item.path" :to="item.path" class="block px-5 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-brand-gold hover:text-brand-dark transition-colors">{{ item.name }}</router-link>
              </div>
            </div>

            <!-- Expenses -->
            <div class="relative group">
              <button class="px-4 py-2 rounded-xl text-sm font-black hover:bg-white/10 transition flex items-center gap-2 group-hover:text-brand-gold">
                Expenses
                <svg class="w-4 h-4 text-white/30 group-hover:text-brand-gold/50 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 9l-7 7-7-7" stroke-width="2.5" /></svg>
              </button>
              <div class="absolute left-0 mt-2 w-56 bg-brand-emerald/95 backdrop-blur-2xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-3 transform translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                <router-link v-for="item in expensesMenu" :key="item.path" :to="item.path" class="block px-5 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-brand-gold hover:text-brand-dark transition-colors">{{ item.name }}</router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side: User Menu & Notifications -->
        <div class="flex items-center space-x-6">
          
          <!-- Notifications Bell -->
          <div class="relative group">
            <button class="relative p-2.5 rounded-xl hover:bg-white/10 transition focus:outline-none">
              <svg class="h-6 w-6 text-white/70 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span v-if="common.lowStockItems.length > 0" class="absolute top-1 right-1 h-5 w-5 bg-rose-500 rounded-full flex items-center justify-center text-[10px] font-black pointer-events-none shadow-sm animate-pulse border-2 border-brand-emerald">
                {{ common.lowStockItems.length > 9 ? '9+' : common.lowStockItems.length }}
              </span>
            </button>
            
            <div class="absolute right-0 mt-2 w-80 bg-brand-emerald/95 backdrop-blur-xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right scale-95 group-hover:scale-100 z-50 overflow-hidden">
               <div class="bg-white/5 border-b border-white/10 p-4 flex items-center justify-between">
                  <h3 class="text-xs font-black text-white/50 uppercase tracking-widest">Low Stock Alerts</h3>
                  <span class="text-xs font-bold text-rose-400 bg-rose-500/20 px-2 py-0.5 rounded-full">{{ common.lowStockItems.length }}</span>
               </div>
               <div class="max-h-80 overflow-y-auto p-2" v-if="common.lowStockItems.length > 0">
                  <router-link to="/inventory/items" v-for="item in common.lowStockItems" :key="item.item_no" class="flex items-center gap-3 p-3 hover:bg-white/5 rounded-xl transition cursor-pointer">
                     <div class="h-10 w-10 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center flex-shrink-0 border border-rose-500/20">
                        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3.L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                     </div>
                     <div class="overflow-hidden">
                        <div class="text-sm font-bold truncate">{{ item.item_desc }}</div>
                        <div class="text-[10px] text-white/40 uppercase tracking-wide">
                            Bal: <span class="text-rose-400 font-bold">{{ item.bal }}</span> / Reorder: {{ item.stock_level }}
                        </div>
                     </div>
                  </router-link>
               </div>
               <div v-else class="p-8 text-center text-white/40 text-sm font-bold flex flex-col items-center justify-center gap-3">
                  <div class="h-12 w-12 rounded-full bg-brand-mint/10 flex items-center justify-center text-brand-mint">
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  Healthy Stock Levels
               </div>
            </div>
          </div>

          <!-- User Profile -->
          <div class="relative group">
            <button class="flex items-center space-x-3 p-1.5 rounded-2xl hover:bg-white/10 transition focus:outline-none border border-transparent hover:border-white/10">
               <div class="h-10 w-10 rounded-xl bg-brand-gold flex items-center justify-center font-black text-brand-dark shadow-xl shadow-brand-gold/20 transform group-hover:scale-105 transition-transform">
                {{ auth.fullname.charAt(0) }}
               </div>
               <div class="hidden lg:block text-left pr-2">
                 <div class="text-sm font-black tracking-tight leading-none text-white">{{ auth.fullname }}</div>
                 <div class="text-[9px] text-brand-gold font-black uppercase tracking-[0.2em] mt-1.5 opacity-80">{{ auth.isAdmin ? 'Administrator' : 'Staff' }}</div>
               </div>
            </button>
            <div class="absolute right-0 mt-2 w-56 bg-brand-emerald/95 backdrop-blur-2xl text-white rounded-2xl shadow-2xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-3 transform translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
               <div class="px-5 py-3 border-b border-white/10 lg:hidden">
                <div class="text-[9px] text-brand-gold font-black uppercase tracking-widest">{{ auth.branch.name }}</div>
                <div class="text-sm font-black">{{ auth.fullname }}</div>
              </div>
              <button @click="logout" class="w-full text-left px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center gap-3">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" stroke-width="2.5" /></svg>
                Sign Out
              </button>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <button @click="isOpen = !isOpen" class="lg:hidden p-2.5 rounded-xl hover:bg-white/10 transition text-white">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="!isOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div v-if="isOpen" class="lg:hidden bg-brand-emerald border-t border-white/5 overflow-y-auto max-h-[calc(100vh-5rem)]">
       <div class="p-4 space-y-6">
         <div v-if="auth.isAdmin">
            <div class="text-brand-mint text-[10px] font-bold uppercase tracking-widest px-4 mb-2">Admin</div>
            <div class="grid grid-cols-1 gap-1">
              <router-link v-for="item in adminMenu" :key="item.path" :to="item.path" class="px-4 py-3 rounded-xl hover:bg-white/5 text-sm font-medium transition" @click="isOpen = false">{{ item.name }}</router-link>
            </div>
         </div>
         <div>
            <div class="text-brand-mint text-[10px] font-bold uppercase tracking-widest px-4 mb-2">Tailoring</div>
            <div class="grid grid-cols-1 gap-1">
              <router-link v-for="item in tailoringMenu" :key="item.path" :to="item.path" class="px-4 py-3 rounded-xl hover:bg-white/5 text-sm font-medium transition" @click="isOpen = false">{{ item.name }}</router-link>
            </div>
         </div>
         <div>
            <div class="text-brand-mint text-[10px] font-bold uppercase tracking-widest px-4 mb-2">Inventory</div>
            <div class="grid grid-cols-1 gap-1">
              <router-link v-for="item in inventoryMenu" :key="item.path" :to="item.path" class="px-4 py-3 rounded-xl hover:bg-white/5 text-sm font-medium transition" @click="isOpen = false">{{ item.name }}</router-link>
            </div>
         </div>
         <div>
            <div class="text-brand-mint text-[10px] font-bold uppercase tracking-widest px-4 mb-2">Payroll</div>
            <div class="grid grid-cols-1 gap-1">
              <router-link v-for="item in payrollMenu" :key="item.path" :to="item.path" class="px-4 py-3 rounded-xl hover:bg-white/5 text-sm font-medium transition" @click="isOpen = false">{{ item.name }}</router-link>
            </div>
         </div>
       </div>
    </div>
  </nav>
</template>
